/**
 * Platform Users WRITE error copy (DNKADM-0167). A failed roster LOAD renders through
 * the shared `ScreenError` (DNKADM-0440); this file adds only the write codes on top.
 */
import { ApiError } from '@/api/errors';
import { serverMessage } from '@/api/serverMessage';
import { v2ErrorCode } from '@/api/v2Errors';

/**
 * Inline outcome of a Platform Users WRITE (DNKADM-0167 — invite / role / status /
 * cancel / resend). Distinct from the shared `ScreenError` (the whole-roster
 * load state): a write failure is surfaced inline against the affordance, not as a
 * full-screen error region.
 */
export interface PlatformMemberWriteErrorView {
  /** The v2 business `code` (`ApiError.details.code`) when present, else undefined. */
  code: string | undefined;
  /** HTTP status of the failure (`0` for a non-`ApiError`). */
  status: number;
  /** Short, brand-scrubbed message to render inline against the affordance. */
  message: string;
  /**
   * True when the caller should refresh the invitation/roster list rather than treat
   * this as a hard error — the target moved on under the write
   * (`INVITATION_ALREADY_ACCEPTED`).
   */
  refreshList: boolean;
}

function writeView(
  status: number,
  code: string | undefined,
  message: string,
  refreshList = false,
): PlatformMemberWriteErrorView {
  return { code, status, message, refreshList };
}

/**
 * Classify a thrown error from a Platform Users WRITE into an inline view
 * (DNKADM-0167 §Error table). Branches on the fine-grained v2 business `code`
 * (`ApiError.details.code`, read via {@link v2ErrorCode}) first, then falls back to
 * the HTTP status; server-supplied text is brand-scrubbed via {@link serverMessage}
 * where it is shown (the fixed known-code copy is already brand-neutral). Unknown
 * codes degrade to the status branch, then to the server message, then to a generic
 * line — never a throw.
 */
export function toPlatformMemberWriteErrorView(error: unknown): PlatformMemberWriteErrorView {
  const status = error instanceof ApiError ? error.status : 0;
  const code = v2ErrorCode(error);

  switch (code) {
    case 'ALREADY_ACTIVE_MEMBER':
      return writeView(status, code, 'This person is already a member of this tenant.');
    case 'INVITATION_ALREADY_PENDING':
      return writeView(status, code, 'An invitation is already pending for this email.');
    case 'INVITATION_ALREADY_ACCEPTED':
      return writeView(
        status,
        code,
        'This invitation was already accepted — refreshing the list.',
        true,
      );
    case 'LAST_OWNER_PROTECTED':
      return writeView(
        status,
        code,
        'You can’t demote or disable the last owner — promote another owner first.',
      );
    case 'SELF_MODIFICATION_FORBIDDEN':
      return writeView(status, code, 'You can’t change your own role or status.');
    case 'ROLE_NOT_ALLOWED':
      return writeView(status, code, 'That role can’t be assigned. Choose Owner, Manager, or Front desk.');
    case 'STATUS_NOT_ALLOWED':
      return writeView(status, code, 'That status can’t be set.');
    case 'INVALID_REQUEST':
      return writeView(
        status,
        code,
        serverMessage(error) ?? 'Please check the details and try again.',
      );
    case 'MEMBER_NOT_FOUND':
      return writeView(status, code, 'That platform user was not found — refresh and try again.');
    default:
      break;
  }

  switch (status) {
    case 401:
      return writeView(status, code, 'Your session expired. Please sign in again.');
    case 403:
      return writeView(status, code, 'You don’t have permission to make this change.');
    case 404:
      return writeView(status, code, 'That platform user was not found — refresh and try again.');
    case 409:
      return writeView(
        status,
        code,
        serverMessage(error) ?? 'That change conflicts with the current state — refresh and try again.',
      );
    case 400:
      return writeView(status, code, serverMessage(error) ?? 'Please check the details and try again.');
    case 502:
      return writeView(
        status,
        code,
        'The service is temporarily unavailable. Please try again in a moment.',
      );
    default:
      return writeView(status, code, serverMessage(error) ?? 'Something went wrong. Please try again.');
  }
}
